import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchGamificationBalance,
  fetchGamificationLifetimePoints,
} from "src/redux/actions/coaching/gamification";
import { fetchSkillsForLearner } from "src/redux/actions/coaching/courses";

import { Metrics } from "./metrics";
import { ScrollableContainer } from "src/components/scroller";

import type { CourseSkill } from "src/types/coaching/courses";
import _ from "lodash";
import cx from "classnames";
import styles from "./sideBar.module.scss";


interface LearningsSidebarProps {
  hasGamificationAccess: boolean;
  // eslint-disable-next-line no-unused-vars
  onOpenRewardsCatalog?: () => void;
}

const LeftArrow: React.FC = () => (
  <div className={cx(styles.arrow, styles.arrowLeft)}>
    <i className={cx(styles.icon, "paperflite-arrow-up")} />
  </div>
);

const RightArrow: React.FC = () => (
  <div className={cx(styles.arrow, styles.arrowRight)}>
    <i className={cx(styles.icon, "paperflite-arrow-up")} />
  </div>
);

export const LearningsSidebar: React.FC<LearningsSidebarProps> = ({
  hasGamificationAccess,
  onOpenRewardsCatalog,
}) => {
  const dispatch = useDispatch();

  const { balance, lifetimePoints } = useSelector((state: any) => {
    const gamification = _.get(state, "coaching.gamification", {});
    return {
      balance: _.get(gamification, "balance.item", 0) as number,
      lifetimePoints: _.get(
        gamification,
        "lifetimePoints.item",
        null,
      ) as number | null,
    };
  }, _.isEqual);

  const { items, loading, loaded, error } = useSelector(
    (state: any) => {
      const slice = _.get(state, "courses.learnerSkills", {});
      return {
        items: _.get(slice, "items", []) as CourseSkill[],
        loading: Boolean(_.get(slice, "loading")),
        loaded: Boolean(_.get(slice, "loaded")),
        error: _.get(slice, "error"),
      };
    },
    _.isEqual,
  );
  
  const skillsEarnedLoading = loading || (!loaded && error == null);

  useEffect(() => {
    if (!hasGamificationAccess) return;
    dispatch(fetchGamificationBalance());
    dispatch(fetchGamificationLifetimePoints());
  }, [hasGamificationAccess]);

  useEffect(() => {
    dispatch(fetchSkillsForLearner());
  }, []);

  const lifetimeDisplay = lifetimePoints?.toLocaleString() ?? "0";
  const spendableDisplay = (balance ?? 0).toLocaleString();

  return (
    <aside className={styles.sidebar}>
      {hasGamificationAccess && (
        <>
          <div className={styles.metricsRow}>
            <ScrollableContainer
              distance={300}
              leftArrowComponent={LeftArrow}
              rightArrowComponent={RightArrow}
            >
              <Metrics
                name="overall"
                value={lifetimeDisplay}
                subTitle="points earned till date"
              />
              <Metrics
                name="balance"
                value={spendableDisplay}
                subTitle="points available to redeem"
                valueVariant="warning"
              />
            </ScrollableContainer>
          </div>
  
          <button
            type="button"
            className={styles.rewardCard}
            onClick={() => onOpenRewardsCatalog?.()}
          >
            <div className={styles.rewardCardHeader}>
              <span className={styles.rewardTitle}>Rewards Shop</span>
              <span className={styles.rewardPointsBadge}>
                <i className={cx("paperflite-coins", styles.rewardCoin)} aria-hidden />
                {spendableDisplay}
              </span>
            </div>
            <p className={styles.rewardBody}>
              Browse rewards you can redeem using your earned points
            </p>
            <span className={styles.rewardExplore}>
              <i className="paperflite-right-arrow" /> explore
            </span>
          </button>
        </>
      )}
  
      <div className={styles.sidebarSkillsBlock}>
        {hasGamificationAccess && <div className={styles.divider} />}
        <SkillsEarned skills={items} loading={skillsEarnedLoading} />
      </div>
    </aside>
  );
};


interface SkillsEarnedProps {
  skills: CourseSkill[];
  loading?: boolean;
}

const SkillsEarned: React.FC<SkillsEarnedProps> = ({ skills, loading = false }) => {
  const renderContent = () => {
    if (loading) {
      return (
        <ul className={styles.skillsEarnedTags} role="list" aria-busy="true">
          {_.times(3, (i) => (
            <SkillPillPlaceholder key={i} />
          ))}
        </ul>
      );
    }

    if (skills.length === 0) {
      return <p className={styles.skillsEarnedEmpty}>No skills earned yet.</p>;
    }

    return (
      <ul className={styles.skillsEarnedTags} role="list">
        {skills.map((skill) => {
          const n = Number(skill.value);
          const hasCount = Boolean(skill.value?.trim()) && Number.isFinite(n) && n > 0;
          return (
            <li key={skill.id} className={styles.skillPill}>
              <span className={styles.skillPillName}>{skill.name}</span>
              {hasCount && <span className={styles.skillPillCount}>x{skill.value}</span>}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <section className={styles.skillsEarned} aria-labelledby="skills-earned-heading">
      <h3 className={styles.skillsEarnedTitle}>
        skills earned
      </h3>
      <div className={styles.skillsEarnedScroll}>
        {renderContent()}
      </div>
    </section>
  );
};


const SkillPillPlaceholder: React.FC = () => (
  <li className={cx(styles.skillPill, styles.placeholder)}>
    <div className={cx(styles.skillPillName, styles.block)} />
  </li>
);